/* =============================================================
   unytii — Referral attribution
   -------------------------------------------------------------
   Captures the partner id from a cart-attribute query parameter
   and makes sure it ends up (and stays) on the Shopify cart.

   The id can arrive two ways:
     • Cart permalink — https://unytii.com/cart/<variant>:1?attributes[partner-id]=1234
       Shopify itself sets the cart attribute in this case.
     • Any website URL — https://unytii.com/?attributes[partner-id]=1234
       Shopify does NOT capture this, so this script does.

   Whatever the path, the script:
     1. reads attributes[partner-id] from the URL (if present)
     2. reads the value already on the cart (if any)
     3. persists it (localStorage + 30-day cookie)
     4. writes it onto the cart via the AJAX Cart API so it follows
        the cart through browsing, checkout and onto the order

   Custom links may also carry a discount, e.g.
     https://unytii.com/?attributes[partner-id]=1234&discount=loulou10
   The `discount` code is stored (localStorage + 30-day cookie) and applied
   via the AJAX Cart API (POST /cart/update.js with a `discount` field).
   Some codes only work for a logged-in customer, so a logged-out shopper's
   apply can silently fail (code doesn't land in cart.discount_codes). We
   therefore RE-TRY the stored code on every visit until it actually shows
   up active on the cart, then stop forcing it. So: log in, come back, and
   the worker applies it automatically.

   Rule: newest wins — a value in the URL overrides everything,
   then the cart's current value, then the stored value.

   Include on every storefront page, e.g. in theme.liquid:
     {{ 'referral-attribution.js' | asset_url | script_tag }}
   ============================================================= */
(function () {
  "use strict";

  var ATTR = "partner-id";                 /* cart attribute key            */
  var STORAGE_KEY = "unytii_partner_id";   /* localStorage + cookie name    */
  var DISCOUNT_KEY = "unytii_discount";    /* last discount we applied      */
  var NAME_KEY = "unytii_partner_name";    /* cached {id,name,ts}           */
  var BAR_DISMISS_KEY = "unytii_bar_off";  /* which partner's bar was closed */
  var MAX_AGE_DAYS = 30;                    /* attribution window            */
  var NAME_TTL = 86400000;                 /* 24h cache for the partner name */
  var DEBUG = true;                        /* set to false to silence logs  */

  function log() {
    if (!DEBUG || !window.console) { return; }
    try { console.log.apply(console, ["%c[unytii]", "color:#5b7cff;font-weight:600;"].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* Locale-aware base path ("/" or "/en/") — the official Shopify way */
  function routeBase() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  /* ---- storage helpers ------------------------------------ */
  function getCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  }
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = name + "=" + encodeURIComponent(value) +
      ";expires=" + d.toUTCString() + ";path=/;SameSite=Lax";
  }
  function readStored() {
    try { var v = window.localStorage.getItem(STORAGE_KEY); if (v) { return v; } } catch (e) {}
    return getCookie(STORAGE_KEY);
  }
  function writeStored(value) {
    try { window.localStorage.setItem(STORAGE_KEY, value); } catch (e) {}
    setCookie(STORAGE_KEY, value, MAX_AGE_DAYS);   /* refresh the 30-day window */
  }

  /* Read attributes[partner-id] from the URL, tolerating %5B / %5D encoding */
  function getUrlValue() {
    var search = window.location.search || "";
    try {
      var v = new URLSearchParams(search).get("attributes[" + ATTR + "]");
      if (v != null) { return v.trim(); }
    } catch (e) {}
    var m = search.match(new RegExp("attributes(?:\\[|%5B)" + ATTR + "(?:\\]|%5D)=([^&]*)", "i"));
    return m ? decodeURIComponent(m[1]).trim() : null;
  }

  /* Read a discount code from the URL (?discount=CODE) */
  function getUrlDiscount() {
    var search = window.location.search || "";
    try {
      var v = new URLSearchParams(search).get("discount");
      if (v != null) { return v.trim(); }
    } catch (e) {}
    var m = search.match(/[?&]discount=([^&]*)/i);
    return m ? decodeURIComponent(m[1]).trim() : null;
  }

  /* ---- desired discount persistence (survives login round-trips) ---- */
  function readDesiredDiscount() {
    try { var v = window.localStorage.getItem(DISCOUNT_KEY); if (v) { return v; } } catch (e) {}
    return getCookie(DISCOUNT_KEY);
  }
  function writeDesiredDiscount(code) {
    try { window.localStorage.setItem(DISCOUNT_KEY, code); } catch (e) {}
    setCookie(DISCOUNT_KEY, code, MAX_AGE_DAYS);
  }
  function clearDesiredDiscount() {
    try { window.localStorage.removeItem(DISCOUNT_KEY); } catch (e) {}
    setCookie(DISCOUNT_KEY, "", -1);
  }

  /* Is `code` present AND applicable on the cart? */
  function discountActive(cart, code) {
    if (!cart || !code) { return false; }
    var lc = code.toLowerCase();
    return (cart.discount_codes || []).some(function (d) {
      return d && d.code && d.code.toLowerCase() === lc && d.applicable !== false;
    });
  }

  /* POST the discount to the cart; resolves with { status, cart } */
  function postDiscount(code) {
    log("applying discount", code, "via /cart/update.js …");
    return fetch(routeBase() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ discount: code })
    }).then(function (r) { return r.json().catch(function () { return null; }).then(function (j) { return { status: r.status, cart: j }; }); });
  }

  /* Ensure the desired discount ends up active on the cart. If it isn't
     (e.g. the code needs a logged-in customer), keep it stored and retry
     next visit; once it's active, stop forcing it. */
  function ensureDiscount(code) {
    if (!code) { log("no discount to ensure"); return; }
    fetchCart().then(function (cart) {
      if (discountActive(cart, code)) { log("discount already active on cart:", code); clearDesiredDiscount(); return; }
      return postDiscount(code).then(function (res) {
        log("discount POST → HTTP", res.status, "| cart.discount_codes:", res.cart && res.cart.discount_codes);
        if (discountActive(res.cart, code)) { log("✓ discount now active:", code); clearDesiredDiscount(); }
        else { log("✗ discount NOT active yet — kept for next visit (likely needs the customer to log in):", code); }
      });
    }).catch(function (e) { log("ensureDiscount failed:", e); });
  }

  function postCartAttribute(value) {
    var body = { attributes: {} };
    body.attributes[ATTR] = value;
    log("writing cart attribute", ATTR, "=", value, "…");
    return fetch(routeBase() + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json().catch(function () { return null; }).then(function (j) { return { status: r.status, cart: j }; }); })
      .then(function (res) { log("cart attribute written — HTTP", res.status, "| cart.attributes:", res.cart && res.cart.attributes); return res; })
      .catch(function (e) { log("cart attribute write FAILED:", e); });
  }

  /* ---- referral bar: recognize the partner and greet the shopper ---- */
  /* Config comes from a small Liquid snippet in the theme layout:
       window.UNYTII_SF = { domain, token, version, locale, barText, barClose }
     token = the same shop.metafields.referal.storefront_token used by the panels. */
  function sfConfig() { return window.UNYTII_SF || {}; }

  function cachedName(id) {
    try { var o = JSON.parse(window.localStorage.getItem(NAME_KEY) || "null"); if (o && String(o.id) === String(id) && (Date.now() - o.ts) < NAME_TTL) { return o.name; } } catch (e) {}
    return null;
  }
  function cacheName(id, name) { try { window.localStorage.setItem(NAME_KEY, JSON.stringify({ id: id, name: name, ts: Date.now() })); } catch (e) {} }

  /* Resolve the partner's display name via the Storefront API (cached) */
  function fetchPartnerName(id) {
    var hit = cachedName(id);
    if (hit) { log("partner name (cached):", hit); return Promise.resolve(hit); }
    var cfg = sfConfig();
    if (!cfg.token || !cfg.domain) { log("no window.UNYTII_SF (token/domain) — cannot resolve partner name"); return Promise.resolve(null); }
    var q = "query($id:ID!){node(id:$id){... on Metaobject{ displayName:field(key:\"displayName\"){value} }}}";
    return fetch("https://" + cfg.domain + "/api/" + (cfg.version || "2024-10") + "/graphql.json", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Shopify-Storefront-Access-Token": cfg.token },
      body: JSON.stringify({ query: q, variables: { id: "gid://shopify/Metaobject/" + id } })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var n = j && j.data && j.data.node;
        var name = (n && n.displayName && n.displayName.value) || null;
        log("partner name lookup →", name);
        if (name) { cacheName(id, name); }
        return name;
      })
      .catch(function (e) { log("partner name lookup FAILED:", e); return null; });
  }

  function barDismissed(id) { try { return window.localStorage.getItem(BAR_DISMISS_KEY) === String(id); } catch (e) { return false; } }
  function markBarDismissed(id) { try { window.localStorage.setItem(BAR_DISMISS_KEY, String(id)); } catch (e) {} }

  function renderReferralBar(id, name) {
    if (document.getElementById("unytii-referral-bar")) { return; }
    var cfg = sfConfig();
    var tpl = cfg.barText || "You were referred — you are on %%NAME%%'s platform.";
    var text = tpl.replace("%%NAME%%", name);
    var bar = document.createElement("div");
    bar.id = "unytii-referral-bar";
    bar.setAttribute("role", "status");
    bar.style.cssText = "position:relative;z-index:2147483000;background:#111827;color:#fff;" +
      "font:500 14px/1.4 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
      "text-align:center;padding:11px 46px;";
    var span = document.createElement("span");
    span.textContent = text;
    bar.appendChild(span);
    var btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("aria-label", cfg.barClose || "Close");
    btn.innerHTML = "&times;";
    btn.style.cssText = "position:absolute;top:50%;right:16px;transform:translateY(-50%);background:none;border:0;color:#fff;font-size:22px;line-height:1;cursor:pointer;opacity:.85;";
    btn.addEventListener("click", function () { if (bar.parentNode) { bar.parentNode.removeChild(bar); } markBarDismissed(id); });
    bar.appendChild(btn);
    document.body.insertBefore(bar, document.body.firstChild);
    log("referral bar shown for", name);
  }

  function maybeShowReferralBar(id) {
    if (!id) { return; }
    if (barDismissed(id)) { log("referral bar previously dismissed for", id); return; }
    fetchPartnerName(id).then(function (name) { if (name) { renderReferralBar(id, name); } });
  }

  /* Console helper: unytiiCart() → logs and returns the current cart */
  function fetchCart() {
    return fetch(routeBase() + "cart.js", { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; });
  }
  window.unytiiCart = function () {
    return fetchCart().then(function (cart) {
      var desired = readDesiredDiscount();
      log("current cart:", cart);
      if (cart) {
        log("  attributes:", cart.attributes);
        log("  discount_codes:", cart.discount_codes);
        log("  item_count:", cart.item_count, "| total:", cart.total_price);
      }
      log("  stored partner-id:", readStored(), "| pending discount:", desired || "(none)", "| active:", discountActive(cart, desired));
      return cart;
    });
  };
  /* Force a retry of the stored discount from the console */
  window.unytiiRetryDiscount = function () { ensureDiscount(readDesiredDiscount()); };

  /* ---- main ----------------------------------------------- */
  function init() {
    var urlValue = getUrlValue();
    var urlDiscount = getUrlDiscount();
    if (urlDiscount) { writeDesiredDiscount(urlDiscount); }
    var desiredDiscount = urlDiscount || readDesiredDiscount();
    log("init on", window.location.href);
    log("URL partner-id:", urlValue, "| URL discount:", urlDiscount, "| stored partner-id:", readStored(), "| desired discount:", desiredDiscount);

    /* Apply / retry the desired discount (independent of attribution) */
    ensureDiscount(desiredDiscount);

    fetchCart()
      .then(function (cart) {
        var cartValue = (cart && cart.attributes) ? cart.attributes[ATTR] : null;
        var stored = readStored();

        /* newest wins: URL → cart → stored */
        var active = urlValue || cartValue || stored;
        log("resolved partner-id →", active, "(url:", urlValue, "cart:", cartValue, "stored:", stored, ")");
        if (!active) { log("no partner-id anywhere — nothing to attribute"); return; }

        writeStored(active);
        maybeShowReferralBar(active);

        /* Guarantee it's on the cart (set it if missing or different) */
        if (cartValue !== active) { return postCartAttribute(active); }
        log("cart already has the right partner-id — no write needed");
      })
      .catch(function (e) {
        log("cart read failed, applying known value anyway:", e);
        var active = urlValue || readStored();
        if (active) { writeStored(active); maybeShowReferralBar(active); postCartAttribute(active); }
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
